/**
 * Component: Product form
 * ------------------------------------------------------------------------------
 * Highly coupled scripts to control the behaviour of a product form
 *
 * @namespace productForm
 */
import { formatMoney } from '@shopify/theme-currency';
import Cookies from 'js-cookie';

import { on } from '../helpers/utils';
import cssClasses from '../helpers/cssClasses';
import bind from '../mixins/bind';

import productSwatches from './product-swatches';

/**
 * DOM selectors.
 */
const selectors = {
  addToCart: '[js-product-form="addToCart"]',
  comparePrice: '[js-product-form="comparePrice"]',
  container: '[js-product-form="container"]',
  masterSelector: '[js-product-form="masterSelector"]',
  maxQuantityInput: '[js-product-form="maxQuantityInput"]',
  notification: '[js-product-form="notification"]',
  optionSelector: '[js-product-form="optionSelector"]',
  price: '[js-product-form="price"]',
  priceWrapper: '[js-product-form="priceWrapper"]',
  productJson: '[js-product-json]',
  backInStockBtn: '[js-product-form="emailWhenAvailable"]',
  sku: '[js-product-form="sku"]',
  skuWrapper: '[js-product-form="skuWrapper"]',
};

/**
 * Create a new product form object.
 */
export default (config) => {
  /**
   * Instance settings.
   */
  const settings = {
    enableHistoryState: config.enableHistoryState || false,
  };

  /**
   * DOM node selectors.
   */
  const container = document.querySelector(
    `[js-product-form="${config.container}"]`
  );
  const nodeSelectors = {
    addToCart: container.querySelector(selectors.addToCart),
    comparePrice: container.querySelector(selectors.comparePrice),
    masterSelector: container.querySelector(selectors.masterSelector),
    optionSelector: container.querySelectorAll(selectors.optionSelector),
    price: container.querySelector(selectors.price),
    priceWrapper: container.querySelector(selectors.priceWrapper),
    maxQuantityInput: container.querySelector(selectors.maxQuantityInput),
    productJson: container.querySelector(selectors.productJson),
    sku: container.querySelector(selectors.sku),
    skuWrapper: container.querySelector(selectors.skuWrapper),
  };

  /**
   * Initialise component bind.
   */
  const binder = bind(container);

  /**
   * Global instances.
   */
  const productData = Object.freeze(
    JSON.parse(nodeSelectors.productJson.innerHTML)
  );
  const variantData = productData.variants;

  let currentVariant = {};

  /**
   * Initialise component.
   */
  function init() {
    if (binder.isSet()) {
      return;
    }

    if (currentVariant) {
      currentVariant = getVariantFromOptions();
      showBackInStock(currentVariant);
    }

    setDefaultState();
    setEventListeners();
    setEventBusListeners();
    binder.set();
  }

  /**
   * Set default state.
   */
  function setDefaultState() {
    if (theme.features.productSwatches) {
      productSwatches({
        data: productData,
      }).init();
    }
  }

  /**
   * Update notification state.
   * @param {Object} variant - Current variant object.
   */
  function updateNotificationState(variant) {
    if (isNotificationCreated()) {
      container.querySelector(selectors.notification).remove();
    }

    if (variant) {
      const quantity = getVariantInventoryQuantity(variant);

      if (!quantity) {
        return;
      }

      if (quantity <= theme.features.inventoryThreshold && quantity > 0) {
        document.body.classList.add('notification-visible');
        container.appendChild(createNotification(quantity));
      } else {
        document.body.classList.remove('notification-visible');
      }
    }
  }

  /**
   * Check if notification exists.
   */
  function isNotificationCreated() {
    return Boolean(container.querySelector(selectors.notification));
  }

  /**
   * Create notification node.
   * @param {Number} quantity - Current variant inventory quantity.
   */
  function createNotification(quantity) {
    const message = getLowStockMessage(quantity);
    const notification = document.createElement('div');

    notification.classList.add('notification');
    notification.innerHTML = getNotificationTemplate(message);
    notification.setAttribute('js-product-form', 'notification');

    return notification;
  }

  /**
   * Get notification template.
   * @param {String} message - text to render inside notification.
   */
  function getNotificationTemplate(message) {
    return `
      <div class="notification__text-wrapper">
        <p class="notification__text caption">
          ${message}
        </p>
      </div>
    `;
  }

  /**
   * Get low stock notification message correctly pluralised.
   */
  function getLowStockMessage(quantity) {
    if (quantity === 1) {
      return theme.low_stock_message.single;
    }

    return theme.low_stock_message.multiple.replace('%quantity%', quantity);
  }

  /**
   * Get variant inventory quantity from product-json.liquid variant data.
   */
  function getVariantInventoryQuantity(variant) {
    if (!variantData.length) {
      return 0;
    }

    return Object.keys(variantData)
      .map((key) => {
        if (variantData[key].id === variant.id) {
          return variantData[key].inventory_quantity;
        }

        return null;
      })
      .filter(Boolean)[0];
  }

  /**
   * Set listeners.
   */
  function setEventBusListeners() {
    Frame.EventBus.listen('Variant:priceChange', (response) =>
      onPriceChange(response)
    );

    Frame.EventBus.listen('ProductSwatches:available', (response) =>
      handleSwatchAvailability(response)
    );

    Frame.EventBus.listen('ProductSwatches:optionSelected', (response) =>
      handleOptionChange(response)
    );

    Frame.EventBus.listen('ProductSwatches:reset', () => resetForm());
  }

  /**
   * Set event listeners on select elements.
   */
  function setEventListeners() {
    [...nodeSelectors.optionSelector].forEach((element) => {
      if (element.nodeName === 'SELECT') {
        on('change', element, () => {
          const variant = getVariantFromOptions();
          handleSelectChange();
          showBackInStock(variant);
        });
      }
    });
  }

  /**
   * Get currently selected options from product form.
   * - Works with all form elements with an option selector selector.
   * @return {array} Values of currently selected variants.
   */
  function getCurrentOptions() {
    const currentOptions = [];

    [...nodeSelectors.optionSelector].forEach((element) => {
      const type = element.getAttribute('type');
      const currentOption = {};

      if (type === 'radio' || type === 'checkbox') {
        if (element.checked) {
          currentOption.value = element.value;
          currentOption.index = element.dataset.index;

          currentOptions.push(currentOption);
        }
      } else {
        currentOption.value = element.value;
        currentOption.index = element.dataset.index;

        currentOptions.push(currentOption);
      }
    });

    return currentOptions;
  }

  /**
   * Find variant based on selected values.
   * @return {object || undefined} Found - Variant object from product.variants.
   */
  function getVariantFromOptions() {
    const selectedValues = getCurrentOptions();
    const variants = productData.variants;
    let found = false;

    variants.forEach((variant) => {
      let satisfied = true;

      selectedValues.forEach((option) => {
        if (satisfied) {
          satisfied = option.value === variant[option.index];
        }
      });

      if (satisfied) {
        found = variant;
      }
    });

    return found || null;
  }

  /**
   * Event handler for when a variant price changes.
   * @param {Object} response - Selected variant data.
   */
  function onPriceChange(response) {
    const form = document.querySelector(
      `[js-product-form="${config.container}"]`
    );
    const priceSelector = form.querySelector(selectors.price);

    priceSelector.setAttribute('data-price', response.price);
    priceSelector.innerText = formatMoney(response.price, theme.moneyFormat).split(".")[0];

    if (form.querySelector(selectors.comparePrice)) {
      const comparePriceSelector = form.querySelector(selectors.comparePrice);
      comparePriceSelector.setAttribute(
        'data-price',
        response.compare_at_price
      );

      if (response.compare_at_price > 0) {
        comparePriceSelector.innerText = formatMoney(
          response.compare_at_price,
          theme.moneyFormat
        ).split(".")[0];
        comparePriceSelector.classList.remove(cssClasses.hidden);
      } else {
        comparePriceSelector.innerText = '';
        comparePriceSelector.classList.add(cssClasses.hidden);
      }
    }

    if (theme.features.currencyConverter) {
      Frame.MultiCurrency.CurrencyConverter.convertAll(
        Cookies.get('new_currency')
      );
    }
  }

  /**
   * Handle default availability.
   */
  function handleSwatchAvailability(response) {
    if (!response.formCompleted) {
      disableAddToCart(theme.strings.prompt);
    }
  }
  /**
   * Changes sticky form size when main form size change triggered
   * @param {HTMLElement} element
   */
  function handleSelectSize(element) {
    const stickyForm = document.querySelector('.js-sticky-form__form');
    if (!stickyForm) return;
    const currentSelected = document.querySelector('.c-sticky-form__option.is-selected');
    const variantIdInput = stickyForm?.querySelector('input[name="id"]');
    currentSelected?.classList.remove('is-selected');
    const selectedInput = document.querySelector(`.c-sticky-form__option[data-size="${element.dataset.value}"]`);
    selectedInput?.classList.add('is-selected');
    variantIdInput.value = selectedInput?.dataset.value;
    const label = document.querySelector('.js-sticky-form__size-select').querySelector('.js-sticky-form__selected-option') || null;
    if(label != null){
      const prefix = label.dataset.prefix;
      label.innerHTML = `<span class="c-sticky-form__selected-option--prefix">${prefix}</span> <span class="js-sticky-form__value">${element.dataset.value}</span>`;
    }
  }

  /**
   * Event handler for when an input changes on the form.
   * @param {Object} response - Pay load from option selected.
   */
  function handleOptionChange(response) {
    const sizeLabel = document
      .querySelector('.product-form__option[js-option="size"]')
      .querySelector('.js-product-form__mobile-tab-open')
      .querySelector('.product-form__label-value');
    sizeLabel.textContent = response.target.textContent;
    handleSelectSize(response.target);
    Frame.EventBus.emit('ProductForm:change', {
      currentOptions: getCurrentOptions(),
      currentVariant: response.formCompleted ? getVariantFromOptions() : null,
      currentInventory: getVariantInventoryQuantity(getVariantFromOptions()),
      selectedValue: response.target.dataset.value,
    });

    Frame.EventBus.emit('ProductForm:selectedValue', {
      selectedValue: response.target.dataset.value,
    });

    if (!response.formCompleted) {
      return;
    }

    updateFormState();
  }

  /**
   * Event handler for when a select element changes on the store.
   */
  function handleSelectChange() {
    updateFormState();
  }

  /**
   * Update form state based on current selections.
   */
  function updateFormState() {
    const variant = getVariantFromOptions();

    updateNotificationState(variant);
    updateAddToCartState(variant);
    updateMaxQuantityState(variant);

    if (!variant) {
      return;
    }

    updateMasterSelect(variant);
    updatePrice(variant);

    if (config.on && config.on.variantChange) {
      config.on.variantChange(variant);
    }

    currentVariant = variant;

    if (settings.enableHistoryState) {
      updateHistoryState(variant);
    }
  }

  /**
   * Updates the DOM state of the add to cart button.
   * @param {object} variant - Selected variant.
   */
  function updateAddToCartState(variant) {
    if (isProductPreorder()) {
      nodeSelectors.addToCart.innerText = theme.strings.preOrder;
      return;
    }

    if (variant) {
      enableAddToCart();
      nodeSelectors.priceWrapper.classList.remove(cssClasses.hidden);
    } else {
      disableAddToCart();
      nodeSelectors.priceWrapper.classList.add(cssClasses.hidden);
      nodeSelectors.addToCart.innerText = theme.strings.unavailable;
      return;
    }

    Frame.EventBus.listen('ProductForm:selectedValue', (response) => {
      if (response.selectedValue === variant.option1) {
        showBackInStock(variant);
      }
    });

    if (variant.available) {
      enableAddToCart();
      nodeSelectors.addToCart.innerText = theme.strings.addToCart;
    } else {
      disableAddToCart();
      nodeSelectors.addToCart.innerText = theme.strings.soldOut;
      showBackInStock(variant);
    }
  }

  /**
   * Show or hide the back in stock button.
   * @param {object} variant - Selected variant.
   */
  function showBackInStock(variant) {
    const backInStockBtn = document.querySelector(selectors.backInStockBtn);

    if (backInStockBtn) {
      window.setTimeout(() => {
        backInStockBtn.style.display = variant.available ? 'none' : 'flex';
      }, 100);
    }
  }

  /**
   * Check if the product is available for preorder
   */
  function isProductPreorder() {
    return container.hasAttribute('js-product-preorder') || container.classList.contains('js-product-form-preorder');
  }

  /**
   * Update max quantity value of quantity selectors.
   * @param {object} variant - Currently selected variant.
   */
  function updateMaxQuantityState(variant) {
    const quantity =
      getVariantInventoryQuantity(variant) > 0
        ? getVariantInventoryQuantity(variant)
        : 999;

    nodeSelectors.maxQuantityInput.value = quantity;
  }

  /**
   * Trigger event when variant price changes.
   * @param {object} variant - Currently selected variant.
   */
  function updatePrice(variant) {
    Frame.EventBus.emit('Variant:priceChange', variant);
  }

  /**
   * Update hidden master select of variant change.
   * @param {object} variant - Currently selected variant.
   */
  function updateMasterSelect(variant) {
    nodeSelectors.masterSelector.value = variant.id;
  }

  /**
   * Update history state for product deep linking.
   * @param {object} variant - Currently selected variant.
   */
  function updateHistoryState(variant) {
    if (!window.history.replaceState || !variant) {
      return;
    }

    const newurl = `
      ${window.location.protocol}//${window.location.host}${window.location.pathname}?variant=${variant.id}
    `;
    window.history.replaceState({ path: newurl }, '', newurl);
  }

  /**
   * Reset form and assign prompt text to add to cart button.
   */
  function resetForm() {
    disableAddToCart();

    if (productData.available) {
      nodeSelectors.addToCart.innerText = theme.strings.prompt;
    }
  }

  /**
   * Add disable state to add to cart button.
   * @param {String} label - Text to use on button.
   */
  function disableAddToCart(label = theme.strings.unavailable) {
    nodeSelectors.addToCart.setAttribute('disabled', true);
    nodeSelectors.addToCart.innerText = label;
  }

  /**
   * Set add to cart button to active.
   */
  function enableAddToCart() {
    nodeSelectors.addToCart.removeAttribute('disabled');
    nodeSelectors.addToCart.innerText = theme.strings.addToCart;

    const backInStockBtn = document.querySelector(selectors.backInStockBtn);
    backInStockBtn.style.display = 'none';
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
    getVariantFromOptions,
  });
};
